import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsCheckCircle, BsChevronDown, BsExclamationTriangle } from 'react-icons/bs';

import { toolDataRows } from '../../utils/toolDataRows';
import { toolErrorText } from '../../utils/toolErrors';
import { describeAction } from '../../utils/toolLabels';

import EnrichmentProgress from './EnrichmentProgress';

const StatusIcon = ({ status }) => {
	if (status === 'ok') return <BsCheckCircle className="h-4 w-4 flex-shrink-0 text-success-700" />;
	if (status === 'error') return <BsExclamationTriangle className="h-4 w-4 flex-shrink-0 text-caution-500" />;
	return (
		<span className="h-3.5 w-3.5 flex-shrink-0 animate-spin rounded-full border-2 border-bgBase-500 border-t-primary-500" />
	);
};

StatusIcon.propTypes = { status: PropTypes.oneOf(['ok', 'error', 'pending']).isRequired };

const StepRow = ({ step }) => {
	const params = step.input ? toolDataRows(step.input) : [];
	const failed = step.status === 'error';
	return (
		<li className="space-y-0.5">
			<div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
				<StatusIcon status={step.status} />
				<span className="text-xs text-text">{describeAction(step)}</span>
			</div>
			{params.length > 0 && (
				<div className="flex flex-wrap gap-x-3 gap-y-0.5 pl-6 text-xs text-bgBase-800">
					{params.map((row) => (
						<span key={row.label} className="whitespace-nowrap">
							{row.label}: <span className="text-text">{row.value}</span>
						</span>
					))}
				</div>
			)}
			{failed && (
				<p className="pl-6 text-xs text-warning-700">
					{toolErrorText(step.error ?? step.summary?.error) ?? 'Fehlgeschlagen.'}
				</p>
			)}
		</li>
	);
};

StepRow.propTypes = { step: PropTypes.object.isRequired };

/**
 * One activity of the assistant (e.g. all company searches of a turn) as a compact, expandable
 * card: status, plain title and a one-line summary in the header, the individual steps with
 * their parameters behind a click.
 */
const StepGroupCard = ({ group }) => {
	const [open, setOpen] = useState(false);
	const running = group.steps.find((step) => step.status === 'pending');
	const stale = group.steps.some((step) => step.summary?.stale);
	const enrichmentJobId = running?.tool === 'start_enrichment' ? running.summary?.jobId : null;

	return (
		<div className="overflow-hidden rounded-card border border-bgBase-500 bg-white">
			<button
				type="button"
				onClick={() => setOpen((value) => !value)}
				aria-expanded={open}
				className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-bgBase-300"
			>
				<StatusIcon status={group.status} />
				<span className="whitespace-nowrap text-sm font-medium text-text">{group.title}</span>
				<span className="ml-auto truncate text-xs text-bgBase-800">
					{running ? describeAction(running) : (group.summary ?? '')}
				</span>
				<BsChevronDown
					className={`h-3 w-3 flex-shrink-0 text-bgBase-700 transition-transform ${open ? 'rotate-180' : ''}`}
				/>
			</button>
			{enrichmentJobId && <EnrichmentProgress jobId={enrichmentJobId} />}
			{stale && (
				<p className="px-3 pb-2 text-xs text-caution-700">
					Veraltet: Die Kriterien wurden während der Prüfung geändert. Für Firmen aus dieser Prüfung werden
					keine Credits ausgegeben.
				</p>
			)}
			{open && (
				<ol className="space-y-2 border-t border-bgBase-400 px-3 pb-2.5 pt-2">
					{group.steps.map((step) => (
						<StepRow key={step.operationId ?? step.seq} step={step} />
					))}
				</ol>
			)}
		</div>
	);
};

StepGroupCard.propTypes = {
	group: PropTypes.shape({
		title: PropTypes.string.isRequired,
		status: PropTypes.oneOf(['ok', 'error', 'pending']).isRequired,
		summary: PropTypes.string,
		steps: PropTypes.arrayOf(
			PropTypes.shape({
				tool: PropTypes.string.isRequired,
				status: PropTypes.string.isRequired,
				input: PropTypes.object,
				summary: PropTypes.object,
				error: PropTypes.string,
			}),
		).isRequired,
	}).isRequired,
};

export default StepGroupCard;
